import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, UseMutationOptions } from 'react-query';

import { getRequest, putRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import {
  DataObjectSchema,
  DataObjectSchemaWithFields,
  DataObjectSchemaUpdateRequestPayload,
} from 'types/dataObjectSchemas';
import { APIError } from 'utils/errors';

const apiUrl = (modelId: StringifiedUUID) => `/api/v1/tq/hierarchy-models/${modelId}/data-object-schemas/`;

type DataObjectSchemasParams = {
  territoryViewId?: StringifiedUUID | null;
  includeOnlyTerritoryViews?: boolean;
};

const getDataObjectSchemas = (modelId: StringifiedUUID, params: DataObjectSchemasParams = {}) => {
  const queryParams = new URLSearchParams();
  if (params.territoryViewId) {
    queryParams.set('territory_view_id', params.territoryViewId);
  }
  if (params.includeOnlyTerritoryViews) {
    queryParams.set('include_only_territory_views', 'true');
  }
  const queryString = queryParams.toString();
  return getRequest<null, APIList<DataObjectSchema>>(`${apiUrl(modelId)}${queryString ? `?${queryString}` : ''}`);
};

const getDataObjectSchemasQueryKey = (modelId: StringifiedUUID, territoryViewId?: StringifiedUUID | null) => [
  'hierarchy-models',
  modelId,
  'data-object-schemas',
  territoryViewId ?? 'main',
];

const getAllTerritoryViewSchemasQueryKey = (modelId: StringifiedUUID) => [
  'hierarchy-models',
  modelId,
  'data-object-schemas',
  'all-territory-views',
];

const useGetDataObjectSchemasQuery = (modelId: StringifiedUUID, params: DataObjectSchemasParams = {}) => {
  const QUERY_KEY = getDataObjectSchemasQueryKey(modelId, params.territoryViewId);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<DataObjectSchema>, APIError>(QUERY_KEY, () => getDataObjectSchemas(modelId, params), {
    enabled: !!modelId && modelId.length > 0,
  });
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);
  return { query, invalidateQuery };
};

type UseGetAllTerritoryViewSchemasQueryOptions = {
  enabled?: boolean;
};

const useGetAllTerritoryViewSchemasQuery = (
  modelId: StringifiedUUID,
  options: UseGetAllTerritoryViewSchemasQueryOptions = {},
) => {
  const { enabled = true } = options;
  const QUERY_KEY = getAllTerritoryViewSchemasQueryKey(modelId);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<DataObjectSchema>, APIError>(
    QUERY_KEY,
    () => getDataObjectSchemas(modelId, { includeOnlyTerritoryViews: true }),
    { enabled: enabled && !!modelId && modelId.length > 0 },
  );
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);
  return { query, invalidateQuery };
};

const getDataObjectSchema = ({
  modelId,
  dataObjectSchemaId,
  dataObjectId,
  includeExportable = false,
  forCompare = false,
  territoryViewId,
}: {
  modelId: StringifiedUUID;
  dataObjectSchemaId: StringifiedUUID;
  dataObjectId: StringifiedUUID;
  includeExportable: boolean;
  forCompare: boolean;
  territoryViewId?: StringifiedUUID | null;
}) => {
  const queryParams: Record<string, any> = {
    exportable: includeExportable,
    'data-object-id': dataObjectId,
  };
  if (forCompare) {
    queryParams.for_compare = true;
  }
  if (territoryViewId !== undefined) {
    queryParams['territory-view-id'] = territoryViewId ?? '';
  }
  return getRequest<Record<string, any>, DataObjectSchemaWithFields>(
    `${apiUrl(modelId)}${dataObjectSchemaId}/`,
    queryParams,
  );
};
const getDataObjectSchemaQueryKey = (
  modelId: StringifiedUUID,
  dataObjectSchemaId: StringifiedUUID,
  dataObjectId: StringifiedUUID,
  includeExportable?: boolean,
  territoryViewId?: StringifiedUUID | null,
) => [
  'hierarchy-models',
  modelId,
  'data-object-schemas',
  dataObjectSchemaId,
  'data-object-id',
  dataObjectId,
  includeExportable ? 'exportable' : 'standard',
  'territory-view',
  territoryViewId ?? 'main',
];

type UseGetDataObjectSchemaQueryOptions = {
  includeExportable?: boolean;
  forCompare?: boolean;
  enabled?: boolean;
  territoryViewId?: StringifiedUUID | null;
};

const useGetDataObjectSchemaQuery = (
  modelId: StringifiedUUID,
  dataObjectSchemaId: StringifiedUUID,
  dataObjectId: StringifiedUUID,
  options: UseGetDataObjectSchemaQueryOptions = {},
) => {
  const { includeExportable = false, forCompare = false, enabled = true, territoryViewId } = options;
  const QUERY_KEY = getDataObjectSchemaQueryKey(
    modelId,
    dataObjectSchemaId,
    dataObjectId,
    includeExportable,
    territoryViewId,
  );
  const queryClient = useQueryClient();

  const query = useQuery<DataObjectSchemaWithFields, APIError>(
    QUERY_KEY,
    () =>
      getDataObjectSchema({
        modelId,
        dataObjectSchemaId,
        dataObjectId,
        includeExportable,
        forCompare,
        territoryViewId,
      }),
    { enabled: enabled && !!modelId && dataObjectSchemaId.length > 0 },
  );
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);
  return { query, invalidateQuery };
};

const updateDataObjectSchema =
  (modelId: StringifiedUUID, dataObjectSchemaId: StringifiedUUID, csrfToken: string) =>
  (payload: DataObjectSchemaUpdateRequestPayload) => {
    return putRequest<DataObjectSchemaUpdateRequestPayload, DataObjectSchema>(
      `${apiUrl(modelId)}${dataObjectSchemaId}/`,
      payload,
      csrfToken,
    );
  };

const useUpdateDataObjectSchemaMutation = (
  modelId: StringifiedUUID,
  dataObjectSchemaId: StringifiedUUID,
  csrfToken: string,
  options: UseMutationOptions<DataObjectSchema, APIError, DataObjectSchemaUpdateRequestPayload> = {},
) => {
  return {
    mutation: useMutation<DataObjectSchema, APIError, DataObjectSchemaUpdateRequestPayload>(
      updateDataObjectSchema(modelId, dataObjectSchemaId, csrfToken),
      options,
    ),
  };
};

/**
 * Hook to invalidate all data object schemas queries for a given model.
 * This includes queries for the main tree, specific territory views, and all-territory-views.
 */
const useInvalidateAllDataObjectSchemasQueries = (modelId: StringifiedUUID) => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries(['hierarchy-models', modelId, 'data-object-schemas']);
  }, [queryClient, modelId]);
};

export const DataObjectSchemasService = {
  apiUrl,
  getDataObjectSchema,
  getDataObjectSchemaQueryKey,
  getDataObjectSchemas,
  getDataObjectSchemasQueryKey,
  getAllTerritoryViewSchemasQueryKey,
  useInvalidateAllDataObjectSchemasQueries,
  useGetDataObjectSchemasQuery,
  useGetDataObjectSchemaQuery,
  useGetAllTerritoryViewSchemasQuery,
  useUpdateDataObjectSchemaMutation,
};
